import React, { useState } from "react";
import { Spin } from "antd";
import { IQuoteLineItem, ISubQuoteProduct } from "../../types";
import { useGetSubQuoteProducts } from "../../hooks";
import { formatDealerAmount } from "@/utils/currency";
import { IDealersCurrency } from "@/types";

const getSubProductOptionType = (p: ISubQuoteProduct): number | null => {
  const raw = p.dogma_optiontype;
  if (raw !== undefined && raw !== null) {
    const n = Number(raw);
    if (!isNaN(n)) return n;
  }
  for (const field of [p.dogma_valuejson, p.dogma_value]) {
    try {
      const parsed = JSON.parse(field || "");
      if (parsed?.optionType !== undefined) return Number(parsed.optionType);
    } catch {}
  }
  if (p.dogma_optionname === "Variation") return 64;
  return null;
};

const filterVariationItems = (items: ISubQuoteProduct[]): ISubQuoteProduct[] => {
  const hasVariation = items.some((p) => getSubProductOptionType(p) === 64);
  return items.filter((p) => {
    const ot = getSubProductOptionType(p);
    if (ot === 0 && hasVariation) return false;
    return true;
  });
};

interface QuoteBreakdownSectionProps {
  lineItems: IQuoteLineItem[];
  subtotal: number;
  discountAmount?: number;
  discountPercentage?: number;
  dealersCurrency?: IDealersCurrency | null;
  exchangeRate?: number;
}

const LineItemBreakdown: React.FC<{
  lineItem: IQuoteLineItem;
  formatAmount: (n: number) => string;
}> = ({ lineItem, formatAmount }) => {
  const qty = lineItem.quantity ?? 1;
  const { data: subProductsData, isLoading } = useGetSubQuoteProducts(lineItem.quotedetailid);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6 border-t border-gray-100">
        <Spin size="small" />
      </div>
    );
  }

  const rawSubs = subProductsData?.value ?? [];

  // Detect foil profile from ALL sub-products (including zero-price ones) —
  // optionType 60 (Profile Colour Type) with 'foil' in responseValue.
  const isFoilProfile = rawSubs.some((sp) => {
    if (getSubProductOptionType(sp) !== 60) return false;
    try {
      const v = JSON.parse(sp.dogma_valuejson || '');
      return v.responseValue?.toLowerCase().includes('foil');
    } catch { return false; }
  });

  // Include negative-price items (reconciliation discounts) — use !== 0 not > 0
  const allSubs = filterVariationItems(
    rawSubs.filter((sp) => ((sp.dogma_vssprice ?? sp.dogma_amount) ?? 0) !== 0)
  );

  // Group all items by category — Additional Charges / Surcharge always rendered last
  const BOTTOM_CATS = new Set(['Surcharge', 'Additional Charges']);
  const byCategory = allSubs.reduce<Record<string, ISubQuoteProduct[]>>((acc, sp) => {
    const cat = sp.dogma_categoryname || "Configuration";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(sp);
    return acc;
  }, {});
  const sortedByCategory = Object.entries(byCategory).sort(([a], [b]) => {
    const aBottom = BOTTOM_CATS.has(a) ? 1 : 0;
    const bBottom = BOTTOM_CATS.has(b) ? 1 : 0;
    return aBottom - bBottom;
  });

  if (allSubs.length === 0) {
    return (
      <p className="text-[11px] text-gray-400 px-5 py-4 text-center border-t border-gray-100">
        No breakdown available
      </p>
    );
  }

  return (
    <div className="border-t border-gray-200">
      {/* Column headers */}
      <div className="flex justify-between px-5 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Item</span>
        <div className="flex gap-10">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-24 text-right">Unit Price</span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-20 text-right">Discount</span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-20 text-right">Net</span>
        </div>
      </div>

      {/* All items grouped by category */}
      {sortedByCategory.map(([cat, items]) => {
        return (
          <div key={cat}>
            {/* Category header */}
            <div className="px-5 py-2 border-b border-gray-100 bg-gray-50/60">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                — {cat}
              </span>
            </div>
            {/* Items */}
            {items.map((sp, i) => {
              let parsedValueJson: { resolverValue?: string; optionType?: number } = {};
              try { parsedValueJson = JSON.parse(sp.dogma_valuejson || ''); } catch { /* empty */ }
              const isFlatCharge =
                parsedValueJson.resolverValue === 'specials_foil_surchage' ||
                parsedValueJson.optionType === 84;
              const vssPrice = sp.dogma_vssprice ?? sp.dogma_amount ?? 0;
              const lineTotal = vssPrice * (isFlatCharge ? 1 : qty);
              const rawDiscount = (sp.dogma_vssdiscountamount ?? 0) * (isFlatCharge ? 1 : qty);
              // Cap discount at max(0, lineTotal) — prevents negative vssPrice from inverting discount sign
              const discount = Math.min(rawDiscount, Math.max(0, lineTotal));
              const ot = getSubProductOptionType(sp);
              const isFoilColour = isFoilProfile && (ot === 34 || ot === 35);
              // Extract "Frame N" from responsePath in stored question JSON
              const frameLabel = (() => {
                try {
                  const msg = JSON.parse(sp.dogma_questionjson ?? '');
                  const rp: string = msg?.question?.responsePath ?? '';
                  const m = rp.match(/frame(\d+)$/i);
                  return m ? `Frame ${m[1]}` : null;
                } catch { return null; }
              })();
              const baseLabel = sp.dogma_optionname && sp.dogma_name
                ? `${sp.dogma_optionname} - ${sp.dogma_name}`
                : sp.dogma_name || sp.dogma_optionname || '';
              const label = isFoilColour
                ? 'Foil Charge'
                : frameLabel ? `${frameLabel} - ${baseLabel}` : baseLabel;
              return (
                <div key={i} className="flex justify-between items-center px-5 py-2.5 border-b border-gray-100 last:border-0">
                  <span className="text-[12.5px] text-gray-800 flex-1">{label}</span>
                  <div className="flex gap-10 flex-shrink-0">
                    <span className="text-[12.5px] text-gray-900 font-medium tabular-nums w-24 text-right">
                      {formatAmount(lineTotal)}
                    </span>
                    <span className="text-[12.5px] text-gray-900 font-medium tabular-nums w-20 text-right">
                      {discount > 0 ? formatAmount(discount) : '—'}
                    </span>
                    <span className="text-[12.5px] text-gray-900 font-medium tabular-nums w-20 text-right">
                      {formatAmount(lineTotal - discount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const LineItemRow: React.FC<{
  lineItem: IQuoteLineItem;
  idx: number;
  formatAmount: (n: number) => string;
}> = ({ lineItem, idx, formatAmount }) => {
  const [expanded, setExpanded] = useState(idx === 0);
  const qty = lineItem.quantity ?? 1;
  const windowName = lineItem.dogma_windowname || lineItem.productname || `Window ${idx + 1}`;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Window header */}
      <button
        type="button"
        className="w-full flex items-start justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-start gap-2">
          <svg
            className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-[13.5px] font-semibold text-gray-900">{windowName}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">QTY: {qty}</p>
          </div>
        </div>
        <span className="text-[13.5px] font-semibold text-gray-900 tabular-nums flex-shrink-0 ml-4 mt-0.5">
          {formatAmount((lineItem.dogma_vsssubtotal ?? 0) - (lineItem.dogma_vssdiscountamount ?? 0))}
        </span>
      </button>

      {expanded && (
        <LineItemBreakdown lineItem={lineItem} formatAmount={formatAmount} />
      )}
    </div>
  );
};

export const QuoteBreakdownSection: React.FC<QuoteBreakdownSectionProps> = ({
  lineItems,
  subtotal,
  discountAmount = 0,
  discountPercentage,
  dealersCurrency,
  exchangeRate,
}) => {
  const formatAmount = (amount: number) =>
    formatDealerAmount(amount, dealersCurrency, exchangeRate);

  return (
    <div className="bg-white rounded-lg p-4 md:p-6">
      <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
        Quote Breakdown
      </h3>

      <div className="space-y-3">
        {lineItems.length === 0 && (
          <p className="text-gray-400 text-center py-6 text-sm">No items</p>
        )}
        {lineItems.map((lineItem, idx) => (
          <LineItemRow
            key={lineItem.quotedetailid}
            lineItem={lineItem}
            idx={idx}
            formatAmount={formatAmount}
          />
        ))}
      </div>

      {lineItems.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal (Gross)</span>
            <span className="font-semibold text-gray-900">{formatAmount(subtotal)}</span>
          </div>

          {discountAmount > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Discount Amount{discountPercentage ? ` (${discountPercentage}%)` : ''}
                </span>
                <span className="text-red-500 font-semibold">-{formatAmount(discountAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span className="text-gray-900">After Discount</span>
                <span className="text-gray-900">{formatAmount(subtotal - discountAmount)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
